import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { CachedContent } from '../../interfaces/cached-content';
import { Token } from '../../interfaces/token';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SocialAuthError } from '../google-auth/google-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AppleAuthService {
  protected cacheService = inject(CacheService);

  protected $document = inject(DOCUMENT);

  protected http = inject(HttpClient);

  protected apiConfig = inject(ApiConfigService);

  constructor() {}

  /**
   * Initiates the login
   */
  public initiateLogin(
    internalRedirectUrl: { url?: string; queryParams?: { [key: string]: string } } | undefined = {},
    subscriberData?: { language?: string; currency?: string }
  ) {
    const redirectUri = `${this.apiConfig.getHost()}/auth/signin`;
    const clientId = (this.cacheService.cachedContent() as CachedContent).cache.config.socialLogin
      .appleClientId;

    const state = encodeURI(
      JSON.stringify({
        type: 'apple',
        internalRedirectUrl: internalRedirectUrl || {},
        language: subscriberData?.language || '',
        currency: subscriberData?.currency || '',
      })
    );
    const appleUrl = `https://appleid.apple.com/auth/authorize?response_type=code&response_mode=form_post&client_id=${clientId}&redirect_uri=${redirectUri}&scope=name%20email&state=${state}`;

    const anchor = this.$document.createElement('a');
    anchor.setAttribute('href', appleUrl);

    this.$document.body.appendChild(anchor);
    anchor.click();
  }

  /**
   * Process apple code for token
   * @param code
   * @param subscriberData Optional language and currency data
   * @returns
   */
  public processAppleCode(
    code: string,
    subscriberData?: { language?: string; currency?: string }
  ): Observable<Token | SocialAuthError> {
    return this.http
      .post<Token>(this.apiConfig.url('subscribers/signin/apple'), {
        socialToken: code,
        customerId: this.cacheService.cachedContent()?.cache.customerId,
        socialTokenType: 'code',
        language: subscriberData?.language || '',
        currency: subscriberData?.currency || '',
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          // force logging for more information
          console.error('Apple auth error:', err);
          const errorMessage = err.error?.message || 'Apple sign-in failed. Please try again.';
          return of({ success: false, error: errorMessage } as SocialAuthError);
        })
      );
  }
}
